import React, { useEffect, useState } from 'react';
import { Typography } from '@duxton-web/ui';
import { useParams } from 'react-router-dom';

import {
  Dimension,
  ExperimentAnalysisConfig,
  getAssignByFacetFromDomain,
  getAssignmentStrategy,
  isViewMode,
  MetricWithDimensionFields,
} from '../../constants';
import { useExperimentContext } from '../../contexts/experiment-context';
import {
  getDimensionsFromAnalysisConfig,
  getMetricTypePrefix,
} from '../../utils';
import { useFetchMetricDimensions } from '@/hooks/usePlannerQueries/analysis';

// Function to determine metric type and get prefix
const getMetricTypeAndPrefix = (
  analysisConfig: ExperimentAnalysisConfig,
  metric: MetricWithDimensionFields,
): string => {
  if (analysisConfig) {
    if (analysisConfig.primary_metrics?.some((m) => m.name === metric.name)) {
      return getMetricTypePrefix('primary');
    }
    if (analysisConfig.secondary_metrics?.some((m) => m.name === metric.name)) {
      return getMetricTypePrefix('secondary');
    }
    if (analysisConfig.guardrail_metrics?.some((m) => m.name === metric.name)) {
      return getMetricTypePrefix('guardrail');
    }
  }
  return '';
};

const formatList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const DeepDiveItem = ({ metric }: { metric: MetricWithDimensionFields }) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { currentAnalysis, analysis, experiment, configurations } =
    experimentContext;
  const currentAnalysisConfig = currentAnalysis?.analysisConfig;
  const analysisConfig = analysis?.analysisConfig;
  const experimentStrategy = getAssignmentStrategy(configurations);
  const experimentDomain = experiment?.domain;
  const assignByFacet = getAssignByFacetFromDomain(experimentDomain)?.name;

  const [metricDimensions, setMetricDimensions] = useState<Dimension[]>([]);

  if (readOnly && currentAnalysisConfig) {
    setMetricDimensions(
      getDimensionsFromAnalysisConfig(currentAnalysisConfig, metric.name),
    );
  }

  const prefix = getMetricTypeAndPrefix(analysisConfig, metric);

  const { data, isLoading } = useFetchMetricDimensions(
    metric.name,
    experimentStrategy,
    metric.join_strategy,
    assignByFacet,
    {
      enabled: !(readOnly && currentAnalysisConfig),
    },
  );

  useEffect(() => {
    setMetricDimensions(data?.dimensions || []);
  }, [data, isLoading]);

  if (isLoading) return null;

  return (
    <li key={metric.name}>
      <Typography.Text strong style={{ color: 'inherit' }}>
        {prefix} {metric.metadata?.display_name ?? metric.name}
      </Typography.Text>
      {metricDimensions.length > 0 ? (
        ` & its dimension(s): ${formatList(metricDimensions.map((d) => d.display_name))}`
      ) : (
        <Typography.Text type="secondary" italic style={{ color: 'inherit' }}>
          {' (No dimensions found)'}
        </Typography.Text>
      )}
    </li>
  );
};

const DeepDive = () => {
  const experimentContext = useExperimentContext();
  const { analysis, experiment } = experimentContext;
  const analysisConfig = analysis?.analysisConfig;
  const deepdiveAllDimensions = analysisConfig?.deepdive_all_dimensions;
  const primaryMetrics = experiment?.metadata?.primaryMetricOptOutEnabled
    ? []
    : analysisConfig?.primary_metrics || [];
  const metrics = [
    ...primaryMetrics,
    ...(analysisConfig?.secondary_metrics || []),
    ...(analysisConfig?.guardrail_metrics || []),
  ];

  let messageText = deepdiveAllDimensions
    ? 'Experiment scorecard will be generated for the below metrics and dimensions:'
    : 'Experiment scorecard will be generated for the below metrics:';

  if (metrics?.length === 0) {
    messageText = 'No metrics have been selected for this experiment.';
  }

  if (!deepdiveAllDimensions)
    return (
      <div>
        <div>{messageText}</div>
        <ul style={{ margin: '8px 0px', paddingLeft: '1.5em' }}>
          {metrics.map((metric) => {
            const prefix = getMetricTypeAndPrefix(analysisConfig, metric);
            return (
              <li key={metric.name}>
                <Typography.Text strong style={{ color: 'inherit' }}>
                  {prefix} {metric.metadata?.display_name ?? metric.name}
                </Typography.Text>
              </li>
            );
          })}
        </ul>
      </div>
    );

  return (
    <div>
      <div>{messageText}</div>
      <ul style={{ margin: '8px 0px', paddingLeft: '1.5em' }}>
        {metrics.map((metric, index) => (
          <DeepDiveItem key={index} metric={metric} />
        ))}
      </ul>
    </div>
  );
};

export default DeepDive;
